import type { Difficulty, FillQuestion } from './types';

// Mulberry32 — small deterministic PRNG seeded by integer
function mulberry32(seed: number): () => number {
  let state = seed | 0;
  return function () {
    state = (state + 0x6d2b79f5) | 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function randInt(rand: () => number, min: number, max: number): number {
  return Math.floor(rand() * (max - min + 1)) + min;
}

/**
 * N-3-2：減法直式計算，含多次退位。
 * difficulty 1：三位數-三位數（單次退位，結果 > 0）
 * difficulty 2：三位數-三位數（多次退位）
 * difficulty 3：四位數-三/四位數（多次退位）
 */
export function generateSubBorrow(seed: number, difficulty: Difficulty): FillQuestion {
  const rand = mulberry32(seed);

  let a: number;
  let b: number;

  if (difficulty === 1) {
    // Force exactly one borrow: ones digit of a < ones digit of b
    do {
      const onesB = randInt(rand, 5, 9);
      const onesA = randInt(rand, 1, onesB - 1); // a ones < b ones → borrow
      const tensA = randInt(rand, 5, 9);
      const tensB = randInt(rand, 1, tensA); // tens don't need borrow
      const hundA = randInt(rand, 2, 9);
      const hundB = randInt(rand, 1, hundA - 1);
      a = hundA * 100 + tensA * 10 + onesA;
      b = hundB * 100 + tensB * 10 + onesB;
    } while (a <= b);
  } else if (difficulty === 2) {
    // Multiple borrows: both ones and tens need to borrow
    do {
      const onesB = randInt(rand, 6, 9);
      const onesA = randInt(rand, 0, onesB - 1);
      const tensB = randInt(rand, 5, 9);
      const tensA = randInt(rand, 0, tensB - 1);
      const hundA = randInt(rand, 3, 9);
      const hundB = randInt(rand, 1, hundA - 1);
      a = hundA * 100 + tensA * 10 + onesA;
      b = hundB * 100 + tensB * 10 + onesB;
    } while (a <= b);
  } else {
    // d3: four-digit minus
    do {
      a = randInt(rand, 2000, 9999);
      b = randInt(rand, 1000, a - 1);
    } while (a - b < 100); // ensure non-trivial result
  }

  const correct = a - b;

  return {
    kind: 'fill',
    prompt: `${a} − ${b} = ?`,
    correctAnswer: String(correct),
    acceptedAnswers: [String(correct)],
    explanation: `${a} 減 ${b}，退位計算後 = ${correct}`,
    kp: 'math.calc.addsub-vertical',
  };
}
